<script lang="ts">
  import { onMount } from 'svelte';
  import { link } from 'svelte-spa-router';
  import { api } from '../../framework/js/api.js';

  export let analysisId: string;

  let analysis: any = null;
  let result: any = null;
  let isLoading = true;
  let isDownloading = false;
  let isDownloadingSource = false;
  let showPastedText = false;
  let jobPollTimer: any = null;

  async function downloadSource() {
    if (isDownloadingSource) return;
    isDownloadingSource = true;
    try {
      await api.downloadGapfinderSource(analysisId, analysis?.source_file_name || null);
    } finally {
      isDownloadingSource = false;
    }
  }

  async function load() {
    isLoading = true;
    try {
      const res: any = await api.getGapfinderAnalysis(analysisId);
      if (res?.success && res?.data) {
        analysis = res.data;
        result = analysis.result;
        if (analysis.generation_status === 'queued' || analysis.generation_status === 'processing') {
          scheduleRefresh();
        }
      }
    } finally {
      isLoading = false;
    }
  }

  function scheduleRefresh() {
    if (jobPollTimer) return;
    jobPollTimer = setInterval(async () => {
      const res: any = await api.getGapfinderAnalysis(analysisId);
      if (res?.success && res?.data) {
        analysis = res.data;
        result = analysis.result;
        if (analysis.generation_status === 'completed' || analysis.generation_status === 'failed') {
          clearInterval(jobPollTimer);
          jobPollTimer = null;
        }
      }
    }, 3000);
  }

  function dimBarColor(score: number | null | undefined): string {
    if (score == null) return '#cccccc';
    if (score >= 80) return '#2b8a3e';
    if (score >= 70) return '#e07a00';
    if (score >= 60) return '#d9822b';
    return '#c92a2a';
  }

  function severityColor(sev: string | null | undefined): string {
    const s = (sev || '').toLowerCase();
    if (s === 'critical') return '#c92a2a';
    if (s === 'moderate') return '#e07a00';
    if (s === 'minor') return '#2b6cb0';
    return '#888';
  }

  function scoreBandColor(band: string | null | undefined): string {
    const b = (band || '').toUpperCase();
    if (b === 'STRONG') return '#2b8a3e';
    if (b === 'REWORK') return '#c92a2a';
    return '#e07a00';
  }

  async function download() {
    if (isDownloading) return;
    isDownloading = true;
    try {
      await api.downloadGapfinderPdf(analysisId);
    } finally {
      isDownloading = false;
    }
  }

  $: scoreBand = (result?.score_band || result?.overall_status || 'IMPROVE').toString().toUpperCase();
  $: bandColor = scoreBandColor(scoreBand);
  $: executiveSummary = result?.executive_summary || result?.summary || '';
  $: briefTitle = result?.brief_title || analysis?.title || 'Research Brief Analysis';

  onMount(() => {
    load();
    return () => {
      if (jobPollTimer) clearInterval(jobPollTimer);
    };
  });
</script>

{#if isLoading}
  <div class="state">
    <i class="spinner loading icon"></i>
    <p>Loading analysis…</p>
  </div>
{:else if !analysis}
  <div class="state">
    <p>Analysis not found.</p>
    <a use:link href="/gapfinder">Back to GapFinder</a>
  </div>
{:else if analysis.generation_status === 'queued' || analysis.generation_status === 'processing'}
  <div class="state">
    <div class="loader-ring"></div>
    <h3>Analyzing…</h3>
    <p>Generating recommendations…</p>
  </div>
{:else if analysis.generation_status === 'failed'}
  <div class="state">
    <i class="exclamation triangle icon large"></i>
    <p>Analysis failed: {analysis.generation_error || 'unknown error'}</p>
    <a use:link href="/gapfinder" class="ui button">Start over</a>
  </div>
{:else if result}
  <div class="gf-doc">
    <!-- Top action bar -->
    <div class="top-bar">
      <a use:link href="/gapfinder/history" class="back-link"><i class="angle left icon"></i> All analyses</a>
      <button class="ui basic button" on:click={download} disabled={isDownloading}>
        <i class="download icon"></i> {isDownloading ? 'Downloading…' : 'Download PDF'}
      </button>
    </div>

    <!-- Header bar with GAPFINDER badge + RESEARCH BRIEF ANALYSIS -->
    <div class="gf-header">
      <span class="gf-badge">GAPFINDER</span>
      <div class="gf-header-right">
        <div class="gf-page-title">RESEARCH BRIEF ANALYSIS</div>
        {#if analysis.created_at}
          <div class="gf-date">{new Date(analysis.updated_at || analysis.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
        {/if}
      </div>
    </div>

    <!-- Brief title -->
    <h1 class="brief-title">{briefTitle}</h1>

    <!-- Source panel: tells the user what this analysis was generated from -->
    {#if analysis.source_type === 'xplorit_brief'}
      <div class="source-panel">
        <div class="source-head">
          <i class="file alternate outline icon" aria-hidden="true"></i>
          <span class="source-label">Source</span>
          <span class="source-kind">Built-to-Brief</span>
        </div>
        <div class="source-body">
          {#if analysis.source_uuid}
            <a use:link href={`/briefs/${analysis.source_uuid}`} class="source-link">
              {analysis.source_brief_name || 'Open the original brief'}
              <i class="angle right icon" aria-hidden="true"></i>
            </a>
          {:else}
            <span class="source-muted">Original brief no longer available.</span>
          {/if}
        </div>
      </div>
    {:else if analysis.source_type === 'uploaded_file'}
      <div class="source-panel">
        <div class="source-head">
          <i class="file outline icon" aria-hidden="true"></i>
          <span class="source-label">Source</span>
          <span class="source-kind">Uploaded file</span>
        </div>
        <div class="source-body">
          <span class="source-filename">{analysis.source_file_name || 'Uploaded document'}</span>
          {#if analysis.source_has_file}
            <button
              type="button"
              class="ui basic button source-download"
              on:click={downloadSource}
              disabled={isDownloadingSource}
            >
              <i class="download icon" aria-hidden="true"></i>
              {isDownloadingSource ? 'Downloading…' : 'Download'}
            </button>
          {:else}
            <span class="source-muted">(original file no longer available)</span>
          {/if}
        </div>
      </div>
    {:else if analysis.source_type === 'pasted_text'}
      <div class="source-panel">
        <div class="source-head">
          <i class="paste icon" aria-hidden="true"></i>
          <span class="source-label">Source</span>
          <span class="source-kind">Pasted text</span>
          {#if analysis.source_content}
            <button
              type="button"
              class="source-toggle"
              on:click={() => (showPastedText = !showPastedText)}
            >
              {showPastedText ? 'Hide' : 'Show'} original text
            </button>
          {/if}
        </div>
        {#if showPastedText && analysis.source_content}
          <pre class="source-pasted">{analysis.source_content}</pre>
        {/if}
      </div>
    {/if}

    <!-- Score + Executive Summary -->
    <div class="score-card" style="border-left-color: {bandColor};">
      <div class="score-col">
        <div class="score-label">SCORE</div>
        <div class="score-number">{result.overall_score ?? '—'}</div>
        {#if scoreBand}
          <div class="score-pill" style="background: {bandColor};">{scoreBand}</div>
        {/if}
      </div>
      <div class="score-divider" style="background: {bandColor};"></div>
      <div class="summary-col">
        <div class="summary-label">EXECUTIVE SUMMARY</div>
        <p class="summary-text prose">{executiveSummary}</p>
      </div>
    </div>

    <!-- Dimension Analysis -->
    {#if Array.isArray(result.dimensions) && result.dimensions.length}
      <h2 class="section-heading">Dimension Analysis</h2>
      <div class="dimensions">
        {#each result.dimensions as dim}
          {@const s = typeof dim.score === 'number' ? dim.score : parseInt(dim.score)}
          {@const color = dimBarColor(s)}
          {@const width = !isNaN(s) ? Math.max(2, Math.min(100, s)) : 0}
          <div class="dim-row">
            <div class="dim-head">
              <span class="dim-name">{dim.name || 'Dimension'}</span>
              <span class="dim-score" style="color: {color};">{isNaN(s) ? '—' : s}<span class="slash">/100</span></span>
            </div>
            <div class="dim-bar-bg">
              <div class="dim-bar-fill" style="width: {width}%; background: {color};"></div>
            </div>
            {#if dim.description}
              <p class="dim-desc">{dim.description}</p>
            {/if}
          </div>
        {/each}
      </div>
    {/if}

    <!-- Identified Gaps -->
    {#if Array.isArray(result.gaps) && result.gaps.length}
      <h2 class="section-heading">Identified Gaps</h2>
      <div class="gaps">
        {#each result.gaps as gap}
          <div class="gap-card">
            <div class="gap-head">
              <span class="gap-tag">{(gap.dimension || 'General').toUpperCase()}</span>
              <span class="gap-sev" style="background: {severityColor(gap.severity)};">
                {(gap.severity || '').toUpperCase()}
              </span>
            </div>
            {#if gap.issue}
              <div class="gap-label">ISSUE</div>
              <p class="gap-body">{gap.issue}</p>
            {/if}
            {#if gap.recommendation}
              <div class="gap-label">RECOMMENDATION</div>
              <p class="gap-body">{gap.recommendation}</p>
            {/if}
          </div>
        {/each}
      </div>
    {/if}

    <div class="bottom-actions">
      <a use:link href="/gapfinder" class="new-btn">Analyse Another Brief</a>
    </div>
  </div>
{:else}
  <div class="state">
    <p>No result available.</p>
  </div>
{/if}

<style>
  .state {
    text-align: center;
    padding: 4rem 1rem;
    color: var(--grey-secondary);
  }

  .loader-ring {
    width: 52px;
    height: 52px;
    border: 4px solid #f2e0c2;
    border-top-color: #e07a00;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1.25rem;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .gf-doc {
    margin: 0 auto;
    color: #2b3a4a;
  }

  .top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .back-link { color: var(--grey-secondary); }

  .gf-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 3px solid #e07a00;
    padding-bottom: 12px;
    margin-bottom: 22px;
  }

  .gf-badge {
    display: inline-block;
    background: #e07a00;
    color: #fff;
    font-weight: 700;
    letter-spacing: 3px;
    padding: 8px 20px;
    border-radius: 8px;
    font-size: 13px;
  }

  .gf-header-right { text-align: right; }

  .gf-page-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 2px;
    color: #6b7280;
  }

  .gf-date {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }

  .brief-title {
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.15;
    margin: 0 0 1.5rem 0;
  }

  .score-card {
    background: #faf8f4;
    border-radius: 10px;
    padding: 1.25rem 1.5rem;
    display: flex;
    align-items: stretch;
    gap: 0;
    margin-bottom: 2rem;
    border-left: 4px solid #e07a00;
  }

  .score-col {
    flex-shrink: 0;
    width: 140px;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .score-label {
    font-size: 11px;
    letter-spacing: 2px;
    color: #6b7280;
  }

  .score-number {
    font-size: 3rem;
    font-weight: 700;
    color: #e07a00;
    line-height: 1;
    margin: 0.25rem 0;
  }

  .score-pill {
    display: inline-block;
    background: #e07a00;
    color: #fff;
    padding: 4px 18px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    margin-top: 0.25rem;
  }

  .score-divider {
    width: 3px;
    background: #e07a00;
    margin: 0 1.25rem;
    border-radius: 2px;
  }

  .summary-col { flex: 1; }

  .summary-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    color: #6b7280;
    margin-bottom: 0.5rem;
  }

  .summary-text {
    line-height: 1.55;
    color: #374151;
    margin: 0;
  }

  .section-heading {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 2rem 0 1rem 0;
  }

  .dimensions {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .dim-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .dim-name {
    font-size: 1rem;
    font-weight: 700;
    color: #1f2937;
  }

  .dim-score {
    font-size: 1rem;
    font-weight: 700;
  }

  .slash {
    color: #9ca3af;
    font-weight: 400;
  }

  .dim-bar-bg {
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    margin: 0.4rem 0 0.5rem 0;
    overflow: hidden;
  }

  .dim-bar-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.4s;
  }

  .dim-desc {
    color: #4b5563;
    line-height: 1.5;
    font-size: 0.9rem;
    margin: 0;
  }

  .gaps {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .gap-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1rem 1.25rem;
    background: #fff;
  }

  .gap-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .gap-tag {
    font-size: 10px;
    font-weight: 700;
    color: #6b7280;
    letter-spacing: 1.5px;
  }

  .gap-sev {
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 3px 12px;
    border-radius: 4px;
  }

  .gap-label {
    color: #6b7280;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    margin-top: 0.5rem;
  }

  .gap-body {
    color: #374151;
    line-height: 1.5;
    font-size: 0.9rem;
    margin: 2px 0 0 0;
  }

  .bottom-actions {
    display: flex;
    justify-content: center;
    margin: 2.5rem 0 1rem;
  }

  .new-btn {
    background: #e07a00;
    color: #fff;
    padding: 0.85rem 2rem;
    border-radius: 9999px;
    font-weight: 500;
    text-decoration: none;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
  }

  .new-btn:hover { filter: brightness(0.95); color: #fff; }

  .source-panel {
    background: var(--white);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    padding: 0.9rem 1.1rem;
    margin: 0 0 1.25rem 0;
  }

  .source-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .source-head i.icon {
    margin: 0;
    color: var(--grey-secondary);
  }

  .source-label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--grey-secondary);
  }

  .source-kind {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--grey);
  }

  .source-body {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.5rem;
    flex-wrap: wrap;
  }

  .source-link {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    color: var(--primary);
    text-decoration: none;
    font-weight: 500;
  }

  .source-link:hover { text-decoration: underline; }

  .source-filename {
    color: var(--grey);
    font-weight: 500;
    word-break: break-all;
  }

  .source-download.ui.basic.button {
    padding: 0.4rem 0.75rem;
    font-size: 0.85rem;
  }

  .source-muted {
    color: var(--grey-secondary);
    font-size: 0.9rem;
  }

  .source-toggle {
    margin-left: auto;
    background: none;
    border: none;
    color: var(--primary);
    cursor: pointer;
    font: inherit;
    font-size: 0.875rem;
    font-weight: 500;
    padding: 0;
  }

  .source-toggle:hover { text-decoration: underline; }

  .source-pasted {
    margin: 0.75rem 0 0 0;
    padding: 0.75rem 1rem;
    background: var(--greyish, #f4f5f7);
    border-radius: 8px;
    max-height: 320px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.875rem;
    color: var(--grey);
    font-family: inherit;
  }
</style>
